/* CSS owns motion. JavaScript only handles direct input and section lifecycle. */
(() => {
  if (customElements.get('wsc-image-compare')) return;
  const positions = new Map();
  class WscImageCompare extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-compare') || this.controller) return;
      this.handle = this.querySelector('[role="slider"]');
      if (!this.handle) return;
      this.controller = new AbortController();
      const listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: this.controller.signal });
      this.inView = !('IntersectionObserver' in window);
      this.updateAnimationVisibility();
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.inView = entries.some((entry) => entry.target === this && entry.isIntersecting);
          this.updateAnimationVisibility();
        });
        this.visibilityObserver.observe(this);
      }
      listen(document, 'visibilitychange', () => this.updateAnimationVisibility());
      this.key = this.dataset.stateKey;
      this.handle.hidden = false;
      this.setAttribute('data-ready', '');
      if (positions.has(this.key)) {
        const saved = positions.get(this.key);
        this.setPosition(saved.value, saved.resumeAt);
      }
      else if (window.CSS?.registerProperty && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.setAttribute('data-animated', '');
      this.updateValue(this.position());
      listen(this, 'pointerdown', (event) => this.start(event));
      listen(window, 'pointermove', (event) => this.move(event));
      listen(window, 'pointerup', (event) => this.end(event));
      listen(window, 'pointercancel', (event) => this.end(event, true));
      listen(this, 'lostpointercapture', (event) => {
        // Touch capture transfers from the original child to this host. Its
        // bubbling loss is not the end of the host's active gesture.
        if (event.target === this) this.end(event, true);
      });
      listen(this, 'dragstart', (event) => event.preventDefault());
      listen(this, 'click', (event) => {
        if (this.suppressClick && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); }
        this.suppressClick = false;
      }, { capture: true });
      listen(this.handle, 'focus', () => this.setPosition(this.position()));
      listen(this.handle, 'click', () => this.setPosition(this.position()));
      listen(this.handle, 'keydown', (event) => {
        let next = this.position();
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= 5;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += 5;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = 100;
        else return;
        event.preventDefault();
        this.setPosition(next);
      });
      listen(this.handle, 'keyup', (event) => {
        if (['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'Home', 'End'].includes(event.key)) this.setPosition(this.position());
      });
      listen(window, 'blur', () => {
        if (this.gesture) this.end({ pointerId: this.gesture.id }, true);
      });
    }
    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      this.controller?.abort();
      this.controller = null;
      this.gesture = null;
      clearTimeout(this.resumeTimer);
    }
    updateAnimationVisibility() {
      this.toggleAttribute('data-animation-paused', !this.inView || document.hidden);
    }
    position() {
      return parseFloat(getComputedStyle(this).getPropertyValue('--wsc-compare-position')) || 0;
    }
    updateValue(value) {
      const first = Math.round(value);
      this.handle.setAttribute('aria-valuenow', first);
      this.handle.setAttribute('aria-valuetext', this.dataset.valueTemplate.replace('__A__', first).replace('__B__', 100 - first));
    }
    setPosition(value, resumeAt = Date.now() + 5000) {
      clearTimeout(this.resumeTimer);
      value = Math.min(100, Math.max(0, value));
      this.style.setProperty('--wsc-compare-position', `${value}%`);
      this.setAttribute('data-manual', '');
      this.removeAttribute('data-resuming');
      positions.set(this.key, { value, resumeAt });
      this.updateValue(value);
      if (!this.gesture && window.CSS?.registerProperty && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.resumeTimer = setTimeout(() => this.resume(), Math.max(0, resumeAt - Date.now()));
      }
    }
    resume() {
      if (!this.isConnected || this.gesture || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.style.setProperty('--compare-resume-from', `${this.position()}%`);
      positions.delete(this.key);
      this.setAttribute('data-animated', '');
      this.setAttribute('data-resuming', '');
      this.removeAttribute('data-manual');
    }
    start(event) {
      if (!event.isPrimary || event.button !== 0 || this.gesture) return;
      clearTimeout(this.resumeTimer);
      this.suppressClick = false;
      const value = this.position();
      this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, value, axis: null };
      if (this.handle.contains(event.target)) {
        // The handle is an explicit drag target; vertical thumb drift must not
        // select the photo's page-scrolling path.
        this.gesture.axis = 'x';
        this.setPointerCapture(event.pointerId);
        this.setPosition(value);
      }
    }
    move(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 6) {
        gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        this.suppressClick = true;
        if (gesture.axis === 'x') this.setPointerCapture(event.pointerId);
      }
      if (gesture.axis !== 'x') return;
      event.preventDefault();
      const width = this.getBoundingClientRect().width;
      if (width > 0) this.setPosition(gesture.value + dx / width * 100);
    }
    end(event, cancelled = false) {
      if (!this.gesture || event.pointerId !== this.gesture.id) return;
      if (cancelled || this.gesture.axis) this.suppressClick = true;
      this.gesture = null;
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
      if (this.hasAttribute('data-manual')) this.setPosition(this.position());
    }
  }
  customElements.define('wsc-image-compare', WscImageCompare);
})();
